import React, { useState, useEffect, useRef } from "react";
import { listAiReportJobs, aiReportDownload, deleteJob } from "@/services/common";
import { Button, Dropdown, message, Popconfirm } from "antd";
import {
  FileTextOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import userInfo from "@/Components/user";
const user_id = userInfo?.id;

// 导出列表：列出 tag=ai-report 的报告任务，展示名字和状态，成功项可点击下载 PDF
const ReportExportList = (props: any) => {
  const [reportList, setReportList] = useState<any[]>([]);
  const [reportListOpen, setReportListOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPage, setReportPage] = useState(1); // 当前页
  const reportPageRef = useRef(1); // 供事件回调读取最新页
  const [reportTotal, setReportTotal] = useState(0); // 总条数
  const reportPerPage = 10;
  const reportTotalPages = Math.max(1, Math.ceil(reportTotal / reportPerPage));

  // 提交报告任务后图标播放"加入购物车"动画；报告完成后刷新列表状态
  const [animating, setAnimating] = useState(false);
  useEffect(() => {
    const onSubmitted = () => {
      setAnimating(false);
      requestAnimationFrame(() => setAnimating(true));
    };
    const onRefresh = () => {
      loadReportList(reportPageRef.current);
    };
    window.addEventListener("ai-report-submitted", onSubmitted);
    window.addEventListener("ai-report-refresh", onRefresh);
    return () => {
      window.removeEventListener("ai-report-submitted", onSubmitted);
      window.removeEventListener("ai-report-refresh", onRefresh);
    };
  }, []);
// 加载导出报告列表（分页）；silent 为 true 时刷新不显示"加载中..."占位
  const loadReportList = async (page = 1, silent = false) => {
    if (!silent) setReportLoading(true);
    try {
      const res = await listAiReportJobs(userInfo.access_token, page, reportPerPage,user_id); 
      setReportList(res?.data?.data || []);
      setReportTotal(res?.data?.count || res?.data?.data?.length || 0);
      setReportPage(page);
      reportPageRef.current = page;
    } catch (err) {
      message.error("获取导出列表失败");
    } finally {
      if (!silent) setReportLoading(false);
    }
  };

  // 报告状态展示：成功绿色对号、失败红色感叹号、生成中加载图标
  const renderReportStatus = (item: any) => {
    if (item.status === "done") return <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />;
    if (item.status === "error") return <ExclamationCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} />;
    return <LoadingOutlined style={{ color: "#1677ff", fontSize: 18 }} />;
  };

  // 删除报告任务（接口与删除任务一致：DELETE /jobs/{id}）
  const handleReportDelete = async (item: any) => {
    try {
      await deleteJob(item.id, userInfo.access_token);
      message.success("报告已删除");
      loadReportList(reportPageRef.current, true); // 静默刷新，不显示加载中占位
    } catch (err) {
      message.error("删除报告失败");
    }
  };

  // 下载成功状态的报告 PDF（通过报告任务 id）
  const handleReportDownload = async (item: any) => {
    const msgKey = `report-download-${item.id}`;
    try {
      message.success({ content: "正在下载中，请稍后...", key: msgKey, duration: 2 });
      const dlRes = await aiReportDownload(item.id, userInfo.access_token);
      const blob = new Blob([dlRes.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.name || `ai-report-${item.id}`}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      message.success({ content: "报告下载成功", key: msgKey, duration: 2 });
    } catch (err) {
      message.error({ content: "下载报告失败", key: msgKey, duration: 3 });
    }
  };

  return (
    <Dropdown
      trigger={["click"]}
      open={reportListOpen}
      placement="bottomRight"
      arrow
      onOpenChange={(open) => {
        setReportListOpen(open);
        if (open) loadReportList();
      }}
      menu={{
        className: "report-export-menu nav-dropdown-menu",
        items: reportLoading
          ? [{ key: "loading", label: <span style={{ fontWeight: 400 }}>加载中...</span> }]
          : reportList.length === 0
          ? [{ key: "empty", label: <span style={{ fontWeight: 400 }}>暂无导出记录</span> }]
          : [
              ...reportList.map((item: any) => ({
                key: String(item.id),
                style: { border: "none", cursor: item.status === "done" ? "pointer" : "not-allowed" },
                onClick: (info: any) => {
                  // Popconfirm 是 React portal，其弹层内（确认/取消）的点击会沿 React 树冒泡到菜单项，
                  // 需排除删除图标与弹层区域，避免触发下载
                  const t: any = info?.domEvent?.target;
                  if (t?.closest?.("[data-report-delete]")) return;
                  if (t?.closest?.(".ant-popover")) return;
                  if (item.status === "done") handleReportDownload(item);
                },
                icon: (
                  <span style={{ fontSize: 14, marginLeft: "5px", margin: "7px 0 7px 5px" }}>
                    <FileTextOutlined style={{ marginRight: "5px" }} />
                    {item.name}
                  </span>
                ),
                label: (
                  <span
                    style={{ fontSize: 14, margin: "7px 5px 7px 0", display: "inline-flex", alignItems: "center", gap: 8 }}
                    onClick={(e) => {
                      // 拦截来自删除图标 / Popconfirm 弹层（React portal 冒泡）的点击，
                      // 避免触发菜单项的 onClick（收起菜单 / 下载）
                      const t: any = e.target;
                      if (t?.closest?.("[data-report-delete]") || t?.closest?.(".ant-popover")) {
                        e.stopPropagation();
                      }
                    }}
                  >
                    {renderReportStatus(item)}
                    <Popconfirm
                      title="删除该报告？"
                      description="删除后无法恢复"
                      okText="删除"
                      okButtonProps={{ danger: true }}
                      cancelText="取消"
                      onConfirm={() => handleReportDelete(item)}
                    >
                      <span
                        data-report-delete
                        style={{ display: "inline-flex", alignItems: "center" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        <DeleteOutlined
                          style={{ color: "#999", fontSize: 15, cursor: "pointer", display: "inline-flex" }}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      </span>
                    </Popconfirm>
                  </span>
                ),
              })),
              { type: "divider" },
              {
                key: "pager",
                label: (
                  <span
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, fontWeight: 400, width: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      style={{ cursor: reportPage > 1 ? "pointer" : "not-allowed", color: reportPage > 1 ? "#1677ff" : "rgba(0,0,0,0.25)" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (reportPage > 1 && !reportLoading) loadReportList(reportPage - 1, true);
                      }}
                    >上一页</span>
                    <span style={{ color: "rgba(0,0,0,0.65)" }}>
                      第 {reportPage} / {reportTotalPages} 页
                    </span>
                    <span
                      style={{ cursor: reportPage < reportTotalPages ? "pointer" : "not-allowed", color: reportPage < reportTotalPages ? "#1677ff" : "rgba(0,0,0,0.25)" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (reportPage < reportTotalPages && !reportLoading) loadReportList(reportPage + 1, true);
                      }}
                    >下一页</span>
                  </span>
                ),
              },
            ],
      }}
    >
      {/* 圆形图标按钮，样式与用户头像一致；提交报告时播放"加入购物车"弹跳动画 */}
      <Button
        title="导出列表"
        shape="circle"
        className={animating ? "report-export-icon-anim" : undefined}
        style={{
          width: 40,
          height: 40,
          border: "none",
          backgroundColor: "#3275faff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FileTextOutlined style={{ color: "#fff", fontSize: 18 }} />
      </Button>
    </Dropdown>
  );
};

export default ReportExportList;
